import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import Image from "../../shared/component/UI/Image";
import * as RoutePath from "../../shared/utils/routeLink";
import * as Variable from "../../shared/utils/variables";
import * as ActionTypes from "../../store/actions/actionTypes";
import LogoHorizontal from "../../assets/logo/horizontal.svg";
import ForgotPasswordIcon from "../../assets/image/forgot-password.svg";
import ForgotPasswordSentIcon from "../../assets/image/forgot-password-sent.svg";
import "./ForgotPassword.scss";
import { generateRequestOptions } from "../../shared/utils/apiEndPoints";
import { toast } from "react-toastify";
import makeRequest from "../../shared/utils/request";

let USERNAME_REGEX = "^[\\w.@+-]+$"; // eslint-disable-line
let USERNAME_REGEX_PATTERN = new RegExp(/^[\w.@+-]+$/);

const ForgotPassword = ({ ENV_NAME, isAuthenticated, setLoginModalShow, ...props }) => {
	const [errorMessage, setErrorMessage] = useState(null);
	const [username, setUsername] = useState("");
	const [confirmed, setConfirmed] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const [timerEnabled, setTimerEnabled] = useState(false);

	useEffect(() => {
		if (isAuthenticated) props.history.push(RoutePath.ROOT);
	}, [isAuthenticated]); // eslint-disable-line

	const validUsername = () => username && USERNAME_REGEX_PATTERN.test(username);
	const disableTimer = () => setTimerEnabled(false);

	const forgotPassword = async () => {
		if (!validUsername()) return;
		setIsProcessing(true);

		let res = await makeRequest({
			...generateRequestOptions("sendResetLink"),
			data: { login: username },
			json: true,
		});

		if (res?.code === 200) {
			toast.success(Variable.FORGOT_PASSWORD_MAIL_SENT_SHORT[ENV_NAME]);
			setTimerEnabled(true);
			setConfirmed(true);
		} else {
			toast.error(Variable.SOMETHING_WENT_WRONG_TRY_AGAIN[ENV_NAME]);
			setErrorMessage(readErrorMsg(res));
		}

		setIsProcessing(false);
	};

	const readErrorMsg = (res) => {
		if (!res?.detail?.errors) return res?.message || Variable.SOMETHING_WENT_WRONG_TRY_AGAIN[ENV_NAME];
		return res?.detail?.errors?.map((i, idx) => <div key={`errormsg-${idx}`}>{`${i?.field}: ${i?.message}`}</div>);
	};

	return (
		<div className="create-account">
			<div className="flex-all-center w-100 mb-lg">
				<Link to={RoutePath.ROOT}>
					<Image src={LogoHorizontal} alt="logo" />
				</Link>
			</div>
			<div className="ca-form-cont">
				{!confirmed ? (
					<form
						onSubmit={(e) => {
							e.preventDefault();
							forgotPassword();
						}}
						autoComplete="new-password"
					>
						<div className="flex-all-center mb-lg">
							<Image src={ForgotPasswordIcon} alt="logo" />
						</div>
						<h4 className="mb-med text-center">
							<strong>{Variable.FORGOT_PASSWORD[ENV_NAME]}</strong>
						</h4>
						<h4 className="mb-lg text-center">{Variable.FORGOT_PASSWORD_MESSAGE[ENV_NAME]}</h4>
						<div className="form-group mb-0">
							<label htmlFor="login" className="font-weight-medium">
								{Variable.USERNAME[ENV_NAME]}
							</label>
							<input
								id="newUsername"
								type="text"
								className={`form-control ${
									errorMessage || (username && !USERNAME_REGEX_PATTERN.test(username))
										? "border-danger"
										: ""
								}`}
								name="newUsername"
								value={username || ""}
								onChange={(e) => setUsername(e.target.value)}
								placeholder={`${Variable.ENTER[ENV_NAME]} ${Variable.USERNAME[ENV_NAME]}`}
								required
								disabled={isProcessing}
								pattern={USERNAME_REGEX}
								maxLength={150}
							/>
							{username && !USERNAME_REGEX_PATTERN.test(username) && (
								<span className="error-txt">{Variable.USERNAME_ACCEPT[ENV_NAME]}</span>
							)}
						</div>
						{errorMessage && <div className="error-msg mt-1p6rem mb-0">{errorMessage}</div>}
						<button
							type="submit"
							className="btn btn-cta-primary btn-block w-100"
							disabled={!validUsername() || isProcessing}
						>
							{isProcessing ? (
								<div className="spinner-border" role="status">
									<span className="visually-hidden">{Variable.LOADING_LABEL[ENV_NAME]}</span>
								</div>
							) : (
								Variable.SEND[ENV_NAME]
							)}
						</button>
					</form>
				) : (
					<>
						<div className="flex-all-center mb-lg">
							<Image src={ForgotPasswordSentIcon} alt="logo" />
						</div>
						<h4 className="mb-med text-center">
							<strong>{Variable.FORGOT_PASSWORD_MAIL_SENT_SHORT[ENV_NAME]}</strong>
						</h4>
						<h4 className="mb-lg text-center">{Variable.FORGOT_PASSWORD_MAIL_SENT[ENV_NAME]}</h4>
						<div className="flex-all-center">
							<h4 className="me-xxs">{Variable.HAVENT_GOT_EMAIL[ENV_NAME]}</h4>
							{!timerEnabled ? (
								<h4 className="text-label-blue-gradient cursorPointer" onClick={() => forgotPassword()}>
									<strong>{Variable.RESEND[ENV_NAME]}</strong>
								</h4>
							) : (
								<Countdown disableTimer={disableTimer} />
							)}
						</div>
					</>
				)}
				<div className="divider my-xl-custom" />
				<div className="flex-all-center flex-column">
					<Link to={RoutePath.ROOT} className="text-decoration-none">
						<h4 className="text-label-blue-gradient cursorPointer" onClick={() => setLoginModalShow(true)}>
							<strong>{Variable.BACK_TO_HOME[ENV_NAME]}</strong>
						</h4>
					</Link>
				</div>
			</div>
		</div>
	);
};

function Countdown({ disableTimer }) {
	const [counter, setCounter] = useState(60);

	useEffect(() => {
		const timer = counter > 0 && setInterval(() => setCounter(counter - 1), 1000);

		if (counter === 1) disableTimer();

		return () => clearInterval(timer);
	}, [counter]); // eslint-disable-line

	return (
		<h4
			style={{
				color: "#036aa6",
				fontWeight: "600",
				letterSpacing: "0.5px",
			}}
		>
			{`00:${String(counter).padStart(2, "0")}`}
		</h4>
	);
}

const mapStateToProps = (state) => {
	return { isAuthenticated: state.auth.isAuthenticated, ENV_NAME: state.auth.selectedEnvironment || "bhs" };
};

const mapStateToDispatch = (dispatch) => {
	return {
		setLoginModalShow: (payload = true) => {
			if (payload) {
				dispatch({
					type: ActionTypes.OPEN_LOGIN_MODAL,
				});
			} else {
				dispatch({
					type: ActionTypes.CLOSE_LOGIN_MODAL,
				});
			}
		},
	};
};

export default connect(mapStateToProps, mapStateToDispatch)(withRouter(ForgotPassword));
